// Локальная проверка реквизитов российских организаций и банков по контрольным суммам.
// Возвращает null, если значение верно, иначе код проблемы (сообщение — в словаре i18n: validation.*).

export type RequisiteIssue =
  | 'inn_format'
  | 'inn_checksum'
  | 'kpp_format'
  | 'ogrn_format'
  | 'ogrn_checksum'
  | 'bik_format'
  | 'account_format'
  | 'account_checksum';

const digitsOnly = (value: string, lengths: readonly number[]): boolean =>
  lengths.includes(value.length) && /^\d+$/.test(value);

const toDigits = (value: string): number[] => value.split('').map(Number);

function weightedCheck(digits: readonly number[], weights: readonly number[]): number {
  let sum = 0;
  weights.forEach((w, i) => {
    sum += w * (digits[i] ?? 0);
  });
  return (sum % 11) % 10;
}

const INN10 = [2, 4, 10, 3, 5, 9, 4, 6, 8];
const INN12_1 = [7, 2, 4, 10, 3, 5, 9, 4, 6, 8];
const INN12_2 = [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8];

/** ИНН: 10 цифр у юрлица, 12 — у физлица и ИП. */
export function validateInn(value: string): RequisiteIssue | null {
  if (!digitsOnly(value, [10, 12])) return 'inn_format';
  const d = toDigits(value);
  if (d.length === 10) {
    return weightedCheck(d, INN10) === d[9] ? null : 'inn_checksum';
  }
  const ok = weightedCheck(d, INN12_1) === d[10] && weightedCheck(d, INN12_2) === d[11];
  return ok ? null : 'inn_checksum';
}

/** КПП: 4 цифры (код налогового органа), 2 символа причины (цифры или A–Z), 3 цифры. */
export function validateKpp(value: string): RequisiteIssue | null {
  return /^\d{4}[\dA-Z]{2}\d{3}$/.test(value) ? null : 'kpp_format';
}

/** ОГРН (13 цифр) или ОГРНИП (15 цифр). */
export function validateOgrn(value: string): RequisiteIssue | null {
  if (!digitsOnly(value, [13, 15])) return 'ogrn_format';
  const body = BigInt(value.slice(0, -1));
  const modulus = value.length === 13 ? 11n : 13n;
  const expected = Number((body % modulus) % 10n);
  return expected === Number(value.at(-1)) ? null : 'ogrn_checksum';
}

/** БИК: 9 цифр, у российских банков начинается с 04. */
export function validateBik(value: string): RequisiteIssue | null {
  return /^04\d{7}$/.test(value) ? null : 'bik_format';
}

const ACCOUNT_WEIGHTS = [7, 1, 3];

function accountKeyValid(key: string): boolean {
  let sum = 0;
  for (let i = 0; i < key.length; i++) {
    sum += Number(key[i]) * (ACCOUNT_WEIGHTS[i % 3] ?? 0);
  }
  return sum % 10 === 0;
}

function validateAccount(
  account: string,
  bik: string,
  prefix: (bik: string) => string,
): RequisiteIssue | null {
  const bikIssue = validateBik(bik);
  if (bikIssue) return bikIssue;
  if (!digitsOnly(account, [20])) return 'account_format';
  return accountKeyValid(prefix(bik) + account) ? null : 'account_checksum';
}

/** Расчётный счёт: ключ считается по трём последним цифрам БИК и 20 цифрам счёта. */
export function validateSettlementAccount(account: string, bik: string): RequisiteIssue | null {
  return validateAccount(account, bik, (b) => b.slice(-3));
}

/** Корреспондентский счёт: ключ считается по «0», 5–6 цифрам БИК и 20 цифрам счёта. */
export function validateCorrespondentAccount(account: string, bik: string): RequisiteIssue | null {
  return validateAccount(account, bik, (b) => `0${b.slice(4, 6)}`);
}

/**
 * Форма работы сотрудника (подпроект 4): самозанятый (НПД), ИП, физлицо по договору ГПХ, штат. Общая для
 * приглашения (identity), личных данных (org), договоров (documents) и контрактов API.
 */
export const EMPLOYMENT_FORMS = ['npd', 'ip', 'gph', 'staff'] as const;
export type EmploymentForm = (typeof EMPLOYMENT_FORMS)[number];

export const isEmploymentForm = (value: string): value is EmploymentForm =>
  (EMPLOYMENT_FORMS as readonly string[]).includes(value);
